/**
 * Kinetic energy calculator — client-side only, no data sent to Shopify.
 * Number fields: live update only when value is in range; blur commits; avoids overwriting while typing.
 */
(function () {
  var W_MIN = 100;
  var W_MAX = 1500;
  var W_STEP = 1;
  var S_MIN = 100;
  var S_MAX = 400;
  var S_STEP = 1;

  function round1(n) {
    return Math.round(n * 10) / 10;
  }

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  function tryIntInRange(raw, min, max) {
    var t = String(raw == null ? '' : raw).trim();
    if (t === '' || t === '-') return null;
    var p = parseInt(t, 10);
    if (isNaN(p)) return null;
    if (p < min || p > max) return null;
    return p;
  }

  function blurInt(raw, min, max, prev) {
    var t = String(raw == null ? '' : raw).trim();
    if (t === '' || t === '-') return prev;
    var p = parseInt(t, 10);
    if (isNaN(p)) return prev;
    return clamp(p, min, max);
  }

  function getRating(ke) {
    if (ke < 25) {
      return {
        label: 'MINIMAL',
        color: '#888',
        tileBg: '#595959',
        desc: 'Not recommended for hunting. Suitable for target shooting, indoor practice, or small game at very close range. Increase arrow weight or bow draw weight to reach hunting-legal energy levels.',
      };
    }
    if (ke <= 41) {
      return {
        label: 'LIGHT GAME',
        color: '#5cb85c',
        tileBg: '#5cb85c',
        desc: 'Adequate for turkey, small deer, and antelope at reasonable distances. Shot placement is critical at this energy level. Consider optimizing your setup for more margin on larger animals.',
      };
    }
    if (ke <= 65) {
      return {
        label: 'MEDIUM GAME',
        color: 'var(--colorBtnPrimary, #000)',
        tileBg: 'var(--colorBtnPrimary, #000)',
        desc: 'The sweet spot for most whitetail, mule deer, black bear, and hog hunters. Delivers reliable penetration and pass-throughs on broadside shots with quality broadheads.',
      };
    }
    if (ke <= 80) {
      return {
        label: 'HEAVY GAME',
        color: '#f0a500',
        tileBg: '#f0a500',
        desc: 'Well-suited for elk, moose, caribou, and large black bear. Provides the penetration needed for the larger bones and denser tissue of big-bodied animals. Great setup for western big game hunting.',
      };
    }
    return {
      label: 'MAX GAME',
      color: '#c060ff',
      tileBg: '#c060ff',
      desc: 'Exceptional kinetic energy for dangerous and large African game — cape buffalo, grizzly, and similar. Pair with heavy, fixed-blade broadheads and strong arrow components for maximum penetration.',
    };
  }

  function emitAnalytics(root, eventName, extra) {
    if (root.getAttribute('data-analytics') !== 'true') return;
    var sectionId = root.getAttribute('data-section-id') || '';
    var payload = Object.assign({ calculator_type: 'ke', section_id: sectionId }, extra || {});
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    }
    if (typeof window.fbq === 'function') {
      window.fbq('trackCustom', 'ArcheryCalculator', payload);
    }
  }

  function initRoot(root) {
    var wRange = root.querySelector('[data-ke-weight-range]');
    var wNum = root.querySelector('[data-ke-weight-number]');
    var sRange = root.querySelector('[data-ke-speed-range]');
    var sNum = root.querySelector('[data-ke-speed-number]');
    var errEl = root.querySelector('[data-ke-error]');
    var rke = root.querySelector('[data-ke-result-ke]');
    var rmom = root.querySelector('[data-ke-result-mom]');
    var rrating = root.querySelector('[data-ke-result-rating]');
    var ratingTile = root.querySelector('[data-ke-rating-tile]');
    var rbar = root.querySelector('[data-ke-result-bar]');
    var rdesc = root.querySelector('[data-ke-result-desc]');
    var liveAnnounce = root.querySelector('[data-ke-live-announce]');

    var wGr = 800;
    var sFps = 250;
    var trackedView = false;
    var trackedUse = false;
    var liveAnnounceTimer = null;

    function clearRatingTile() {
      if (ratingTile) {
        ratingTile.classList.remove('is-rated');
        ratingTile.style.removeProperty('--calc-ke-rating-bg');
      }
    }

    function setRatingTile(r) {
      if (ratingTile && r) {
        var bg = r.tileBg || r.color;
        if (bg) {
          ratingTile.style.setProperty('--calc-ke-rating-bg', bg);
          ratingTile.classList.add('is-rated');
        }
      }
    }

    function scheduleLiveAnnounce(text) {
      if (!liveAnnounce) return;
      clearTimeout(liveAnnounceTimer);
      liveAnnounceTimer = setTimeout(function () {
        liveAnnounce.textContent = text || '';
      }, 300);
    }

    function syncUIFromState(skipWNum, skipSNum) {
      wGr = clamp(Math.round(wGr), W_MIN, W_MAX);
      sFps = clamp(Math.round(sFps), S_MIN, S_MAX);
      wRange.value = String(wGr);
      sRange.value = String(sFps);
      if (!skipWNum) wNum.value = String(wGr);
      if (!skipSNum) sNum.value = String(sFps);
    }

    function calc() {
      if (errEl) errEl.hidden = true;

      var ke = (wGr * sFps * sFps) / 450240;
      var mom = (wGr * sFps) / 225400;
      var keR = round1(ke);
      var momR = Math.round(mom * 1000) / 1000;

      var r = getRating(ke);
      var barPct = Math.min(100, Math.max(1, (ke / 100) * 100));

      if (rke) rke.innerHTML = keR + '<span class="calc-ke__unit"> ft·lbs</span>';
      if (rmom) rmom.innerHTML = momR + '<span class="calc-ke__unit"></span>';
      if (rrating) rrating.textContent = r.label;
      setRatingTile(r);
      if (rbar) {
        rbar.style.width = barPct.toFixed(1) + '%';
        rbar.style.background = r.color;
      }
      if (rdesc) rdesc.textContent = r.desc;

      scheduleLiveAnnounce(
        'Kinetic energy ' +
          keR +
          ' foot-pounds. Momentum ' +
          momR +
          ' slug feet per second. Hunting class: ' +
          r.label +
          '.'
      );
    }

    function trackUse() {
      if (!trackedUse) {
        trackedUse = true;
        emitAnalytics(root, 'archery_calculator_use', { engagement: 'adjust' });
      }
    }

    function closeHelperDetails() {
      root.querySelectorAll('[data-ke-helper-details]').forEach(function (d) {
        d.open = false;
      });
    }

    root.querySelectorAll('[data-ke-helper-details]').forEach(function (el) {
      el.addEventListener('toggle', function () {
        if (!el.open) return;
        root.querySelectorAll('[data-ke-helper-details]').forEach(function (other) {
          if (other !== el) other.open = false;
        });
      });
    });

    if (wRange) {
      wRange.addEventListener('pointerdown', closeHelperDetails);
    }
    if (sRange) {
      sRange.addEventListener('pointerdown', closeHelperDetails);
    }

    function onWRangeInput() {
      closeHelperDetails();
      var v = parseInt(wRange.value, 10);
      if (isNaN(v)) return;
      wGr = clamp(v, W_MIN, W_MAX);
      syncUIFromState(document.activeElement === wNum, document.activeElement === sNum);
      calc();
      trackUse();
    }

    function onWNumInput() {
      var committed = tryIntInRange(wNum.value, W_MIN, W_MAX);
      if (committed != null) {
        wGr = committed;
        syncUIFromState(true, document.activeElement === sNum);
        calc();
        trackUse();
      }
    }

    function onWNumBlur() {
      wGr = blurInt(wNum.value, W_MIN, W_MAX, wGr);
      syncUIFromState(false, document.activeElement === sNum);
      calc();
    }

    function onSRangeInput() {
      closeHelperDetails();
      var v = parseInt(sRange.value, 10);
      if (isNaN(v)) return;
      sFps = clamp(v, S_MIN, S_MAX);
      syncUIFromState(document.activeElement === wNum, document.activeElement === sNum);
      calc();
      trackUse();
    }

    function onSNumInput() {
      var committed = tryIntInRange(sNum.value, S_MIN, S_MAX);
      if (committed != null) {
        sFps = committed;
        syncUIFromState(document.activeElement === wNum, true);
        calc();
        trackUse();
      }
    }

    function onSNumBlur() {
      sFps = blurInt(sNum.value, S_MIN, S_MAX, sFps);
      syncUIFromState(document.activeElement === wNum, false);
      calc();
    }

    wRange.min = String(W_MIN);
    wRange.max = String(W_MAX);
    wRange.step = String(W_STEP);
    sRange.min = String(S_MIN);
    sRange.max = String(S_MAX);
    sRange.step = String(S_STEP);

    wRange.addEventListener('input', onWRangeInput);
    wNum.addEventListener('input', onWNumInput);
    wNum.addEventListener('blur', onWNumBlur);
    sRange.addEventListener('input', onSRangeInput);
    sNum.addEventListener('input', onSNumInput);
    sNum.addEventListener('blur', onSNumBlur);

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !trackedView) {
            trackedView = true;
            emitAnalytics(root, 'archery_calculator_view', { engagement: 'visible' });
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(root);

    syncUIFromState(false, false);
    calc();
  }

  function initAll() {
    document.querySelectorAll('[data-ke-calculator]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
